import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Button, FileButton, Stack, TFCard, TFHTMLText, TFHTMLTextEditor, TFNotifier } from '@tf/ui';
import { T } from '@tf/utils';
import type React from 'react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileList } from '@/components/cases/FileList';
import { Greetings } from '@/components/cases/Greetings';
import { RequestFileList } from '@/components/cases/RequestFileList';
import { reportCaseRequestSubmitError } from '@/components/cases/reportCaseRequestSubmitError';
import type { AdditionalDocumentsRequest, InformationRequest as InformationRequestType } from '@/core/api';
import { CaseRequestApiError, useSubmitCaseRequestMutation } from '@/core/api';

interface Props {
	caseRequest: InformationRequestType | AdditionalDocumentsRequest;
}

// Must stay in sync with the backend limit
const MAX_ATTACHED_FILES = 20;

export const InformationRequest: React.FC<Props> = ({ caseRequest }) => {
	const navigate = useNavigate();
	const { _ } = useLingui();

	const [editorContent, setEditorContent] = useState('');

	const { requestId } = useParams();
	T.assertNotNullish(requestId, 'no requestId in url');

	const resetFileRef = useRef<() => void>(null);
	const addFile = async (file: File | null) => {
		if (!file) {
			return;
		}
		if (attachedFiles.length >= MAX_ATTACHED_FILES) {
			TFNotifier.error(
				`The number of attached files exceeds the allowed limit. You can attach up to ${MAX_ATTACHED_FILES} files.`,
			);
			return;
		}
		setAttachedFiles((prevFiles) => [...prevFiles, file]);
	};

	const deleteFile = (file: File) => {
		setAttachedFiles((prevFiles) => {
			return prevFiles.filter((f) => f !== file);
		});
	};

	const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

	const submitClientCaseRequestMutation = useSubmitCaseRequestMutation();
	const isSubmitting = submitClientCaseRequestMutation.isPending;

	const submitResponse = () => {
		// Guard against double submits
		if (isSubmitting) {
			return;
		}

		if (!editorContent && caseRequest.caseKind !== 'additional_documents') {
			TFNotifier.error(
				_(
					msg({
						id: 'caseRequestPage.notification.textRequired',
						message: 'Text is required',
					}),
				),
			);
			return;
		}

		submitClientCaseRequestMutation.mutate(
			{
				id: caseRequest.id,
				responseText: editorContent,
				files: attachedFiles,
			},
			{
				onSuccess: () => {
					navigate('/request-submitted');
				},
				onError: (error) => {
					reportCaseRequestSubmitError(error, {
						caseId: caseRequest.id,
						caseKind: caseRequest.caseKind,
						files: attachedFiles,
					});

					if (error instanceof CaseRequestApiError && error.status === 400) {
						TFNotifier.error('This request could not be submitted');
						return;
					}

					TFNotifier.error('Error while submitting request');
				},
			},
		);
	};

	return (
		<TFCard innerPadding="2rem">
			<Greetings caseNumber={caseRequest.caseId} dueDate={caseRequest.dueDate} />
			{caseRequest.requestText ? <TFHTMLText text={caseRequest.requestText} /> : null}
			{caseRequest.requestFiles?.length ? (
				<RequestFileList requestId={caseRequest.id} files={caseRequest.requestFiles} />
			) : null}
			<Box mt="1rem 0">
				<TFHTMLTextEditor content={editorContent} onChange={setEditorContent} />
			</Box>
			{attachedFiles.length > 0 && <FileList files={attachedFiles} onDelete={deleteFile} />}
			<FileButton accept="image/png, image/jpeg, application/pdf" resetRef={resetFileRef} onChange={addFile}>
				{(props) => (
					<Stack gap={9}>
						<Button
							mt="1.5rem"
							size="sm"
							variant="light"
							fullWidth
							{...props}
							disabled={isSubmitting || attachedFiles.length >= MAX_ATTACHED_FILES}
						>
							<Trans id="caseRequestPage.addFileButton">Add file</Trans>
						</Button>
						<Box style={{ fontSize: '0.8rem', color: 'var(--mantine-color-dimmed)' }}>
							<Trans id="caseRequestPage.fileLimitHint">You can attach up to {MAX_ATTACHED_FILES} files.</Trans>
						</Box>
						<Button onClick={submitResponse} loading={isSubmitting} disabled={isSubmitting}>
							<Trans id="caseRequestPage.submitButton">Submit</Trans>
						</Button>
					</Stack>
				)}
			</FileButton>
		</TFCard>
	);
};
